import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { ProfileCatalogProduct, ProductFileUpload, ProductType } from '@/types';
import { getProfileCatalogProduct, saveProfileCatalogProduct } from '@/services/profile-catalog-products';
import { EditProfileCatalogProductWizard } from '../products/edit-product-wizard/EditProductWizard';
import { useGetActiveProfile } from '@/utils/hooks';
import { emptyMeasurement, getMeasurementsFromProduct, updateProductFromMeasurements } from '@/utils/unit-of-measurements';

interface CatalogProductEditDialogProps {
    catalogProductId: number | null;
    show: boolean;
    onClose: () => void;
    onSave: () => void;
    onCancel?: () => void;
    isCustomPaintProduct: boolean;
    isDuplicate?: boolean;
}

export const CatalogProductEditDialog: React.FC<CatalogProductEditDialogProps> = ({
    catalogProductId,
    show,
    onClose,
    onSave,
    onCancel,
    isCustomPaintProduct = false,
    isDuplicate,
}) => {
    const profile = useGetActiveProfile();
    const [product, setProduct] = useState<ProfileCatalogProduct | null>(null);
    const [saving, setSaving] = useState(false);
    const [measurements, setMeasurements] = useState(emptyMeasurement);

    if (!profile) {
        return <LoadingIndicator />;
    }

    const loadData = async () => {
        if (isDuplicate && !catalogProductId) {
            throw new Error('Needs CatalogProductId if isDuplicate');
        }
        if (catalogProductId) {
            const p = await getProfileCatalogProduct(catalogProductId);
            setProduct(p);
            setMeasurements(getMeasurementsFromProduct(p, profile.defaultUnitOfMeasurement));
        } else {
            setProduct(getEmptyProduct() as ProfileCatalogProduct);
        }
    };

    function getEmptyProduct(): Partial<ProfileCatalogProduct> {
        return {
            name: '',
            defaultPrice: null,
            sku: '',
            description: '',
            productLink: '',
            ownerProfileId: profile?.id,
            images: [],
            attachments: [],
            // 500 is Paint
            itemCategoryId: isCustomPaintProduct ? 500 : undefined,
            type: isCustomPaintProduct ? ProductType.CustomPaint : ProductType.Normal,
        };
    }

    function reset() {
        setProduct(null);
        setMeasurements(emptyMeasurement);
        setSaving(false);
    }

    const handleCancelClick = () => {
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };

    const handleSaveClick = async () => {
        setSaving(true);

        const centimeterProduct = updateProductFromMeasurements(product!, measurements, profile.defaultUnitOfMeasurement);
        const { images, attachments, ...cleanProduct } = centimeterProduct!;
        if (isDuplicate) {
            (cleanProduct as Partial<ProfileCatalogProduct>).id = undefined;
        }

        const files = [
            ...images!.map((a) => ({ ...a, type: 'Image' }) as ProductFileUpload),
            ...attachments!.map((a) => ({ ...a, type: 'Attachment' }) as ProductFileUpload),
        ];

        try {
            await saveProfileCatalogProduct(cleanProduct, files);
            triggerHandler(onSave);
            triggerHandler(onClose);
            reset();
        } finally {
            setSaving(false);
        }
    };

    const getHeader = () => {
        if (catalogProductId && isDuplicate) {
            return <Trans>Duplicate product</Trans>;
        }

        if (catalogProductId) {
            return <Trans>Edit product</Trans>;
        }

        return <Trans>Create new product</Trans>;
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={loadData} size="lg">
            <ModalHeader>{getHeader()}</ModalHeader>
            <ModalBody>
                {product ? (
                    <EditProfileCatalogProductWizard
                        catalogProduct={product}
                        productMeasurements={measurements}
                        onProductMeasurementsChange={setMeasurements}
                        onChange={(newProduct) => setProduct(newProduct as ProfileCatalogProduct)}
                        onFinish={handleSaveClick}
                        onCancel={handleCancelClick}
                        saving={saving}
                        allowPriceEdit={true}
                    />
                ) : (
                    <LoadingIndicator />
                )}
            </ModalBody>
        </Modal>
    );
};
